/*
 * Display strings for the Overview's identity header and status banner. Pure,
 * so they are unit-tested directly.
 */
import type {
  ForeignNational,
  StatusHealth
} from "@/api/types/foreignNational";

import { formatShortDate } from "../../header/clientHeaderModel";
import { ageFromBirthday, getFullName } from "../../lib/format";
import { languageLabel } from "../personal/personalModel";

export type Tone = "red" | "amber" | "emerald" | "neutral";

const clean = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** "Dr. Amara Chiamaka Okafor" (title only when set). */
export const titledName = (c: ForeignNational): string =>
  [clean(c.title), getFullName(c)].filter(Boolean).join(" ") ||
  clean(c.email) ||
  "Client";

const joinCountries = (list: string[]): string =>
  list.length <= 1
    ? list[0] ?? ""
    : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;

/** "she/her · 38 · Born in Lagos, Nigeria · Citizen of Nigeria · Speaks Yoruba (interpreter)". */
export const identityLine = (c: ForeignNational): string => {
  const parts: string[] = [];
  if (clean(c.pronouns)) parts.push(clean(c.pronouns));
  const age = ageFromBirthday(c.birthday ?? null);
  if (age !== null) parts.push(String(age));
  const born = [clean(c.city_of_birth), clean(c.country_of_birth)]
    .filter(Boolean)
    .join(", ");
  if (born) parts.push(`Born in ${born}`);
  const citizenships = (
    c.citizenships?.length ? c.citizenships : [clean(c.country_of_citizenship)]
  ).filter(Boolean);
  if (citizenships.length)
    parts.push(`Citizen of ${joinCountries(citizenships)}`);
  const language = languageLabel(c.preferred_language ?? null);
  if (language && c.preferred_language !== "en")
    parts.push(
      `Speaks ${language}${
        c.interpreter_needed ? " (needs an interpreter)" : ""
      }`
    );
  else if (c.interpreter_needed) parts.push("Needs an interpreter");
  return parts.join(" · ");
};

/** Statuses that don't end, so a missing end date isn't a problem. */
const PERMANENT = /citizen|lpr|permanent resident|green card/i;

export const isPermanentStatus = (status?: string | null): boolean =>
  PERMANENT.test(clean(status));

const days = (n: number): string => `${n} day${n === 1 ? "" : "s"}`;

/** The status chip in the header ("H-1B · 47 days left"). */
export const statusChip = (
  status: string | null | undefined,
  health: StatusHealth | null | undefined
): { label: string; tone: Tone } | null => {
  const name = clean(health?.status) || clean(status);
  if (!name) return null;
  if (!health || isPermanentStatus(name))
    return { label: name, tone: "neutral" };
  const left = health.days_left;
  switch (health.state) {
    case "expired":
      return {
        label: `${name} · ended ${days(Math.abs(left ?? 0))} ago`,
        tone: "red"
      };
    case "expiring":
      return { label: `${name} · ${days(left ?? 0)} left`, tone: "amber" };
    case "current":
      return {
        label: health.duration_of_status
          ? `${name} · D/S`
          : health.ends
          ? `${name} · until ${formatShortDate(health.ends)}`
          : name,
        tone: "emerald"
      };
    default:
      return { label: name, tone: "neutral" };
  }
};

export type StatusBannerCopy = {
  tone: Tone;
  title: string;
  body: string;
  prompt: string;
};

/** The banner under the header, or null when the status is fine. */
export const statusBanner = (
  health: StatusHealth | null | undefined,
  firstName: string | null,
  fallbackStatus?: string | null
): StatusBannerCopy | null => {
  if (!health) return null;
  const who = firstName ? `${firstName}'s` : "The client's";
  const status = clean(health.status) || clean(fallbackStatus);
  const named = status ? `${who} ${status} status` : `${who} status`;
  const label = clean(health.governing_label) || "status";
  const ends = health.ends ? formatShortDate(health.ends) : "";
  if (health.state === "expired") {
    const ago = Math.abs(health.days_left ?? 0);
    return {
      tone: "red",
      title: `${named} ended ${ends}, ${days(ago)} ago`,
      body: `Nothing in ${
        firstName ? `${firstName}'s` : "the client's"
      } record shows an extension, a change of status or a later I-94. Check the case documents (Autofill can pull them in) and confirm the current status before filing anything.`,
      prompt: `${named} ended on ${ends} (per the ${label.toLowerCase()} date on file). What could explain it, what should we check, and what are the options?`
    };
  }
  if (health.state === "expiring") {
    return {
      tone: "amber",
      title: `${named} ends ${ends}, in ${days(health.days_left ?? 0)}`,
      body: "Plan the extension or change of status now; USCIS needs the petition filed before the end date.",
      prompt: `${named} ends on ${ends}. What do we need to file before then, and by when?`
    };
  }
  if (health.state === "unknown" && status && !isPermanentStatus(status)) {
    const inline = `${
      firstName ? `${firstName}'s` : "the client's"
    } ${status} status`;
    return {
      tone: "neutral",
      title: `We don't know when ${inline} ends`,
      body: "Add the I-94 or approval notice to the status history, or autofill it from the case documents.",
      prompt: `When does ${inline} end? Check the case documents.`
    };
  }
  return null;
};

/** The banner's one line once minimized ("Status ended 847 days ago"). */
export const statusBannerCompact = (
  health: StatusHealth | null | undefined
): string => {
  const left = health?.days_left ?? 0;
  if (health?.state === "expired")
    return `Status ended ${days(Math.abs(left))} ago`;
  if (health?.state === "expiring") return `Status ends in ${days(left)}`;
  return "Status end date unknown";
};

/** The other lapsed items the banner lists under "Also lapsed". */
export const alsoLapsed = (
  health: StatusHealth | null | undefined
): NonNullable<StatusHealth["expired_items"]> =>
  (health?.expired_items ?? []).filter(
    (i) => i.kind !== health?.governing_kind
  );

/**
 * What a dismissal of the status banner is about: the client, the kind of
 * warning, the status and its end date, and the other lapsed items. Leaves
 * out the day count, so the banner stays dismissed as the days pass.
 */
export const statusBannerKey = (
  clientId: string,
  health: StatusHealth | null | undefined,
  fallbackStatus?: string | null
): string => {
  const status = clean(health?.status) || clean(fallbackStatus);
  const lapsed = alsoLapsed(health)
    .map((i) => `${i.kind}@${i.due.slice(0, 10)}`)
    .sort()
    .join(",");
  return [
    "status",
    clientId,
    health?.state ?? "",
    status,
    (health?.ends ?? "").slice(0, 10),
    lapsed
  ].join("|");
};
